import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  CircleUserRound,
  CreditCard,
  Mail,
  MessageCircleMore,
  ShieldCheck,
} from "lucide-react";

const supportTopics = [
  {
    icon: CircleUserRound,
    eyebrow: "Konto & Zugang",
    title: "Anmeldung und Berechtigungen",
    text: "Hilfe bei Login, Passwort, Mitarbeiterzugängen, Rollen und der Auswahl des richtigen Standorts.",
  },
  {
    icon: CalendarDays,
    eyebrow: "App & Alltag",
    title: "Termine und Funktionen",
    text: "Fragen zu Kalender, Kunden, Messenger, Formularen, Benachrichtigungen oder der mobilen App.",
  },
  {
    icon: CreditCard,
    eyebrow: "Vertrag & Abrechnung",
    title: "Tarif, Zahlungen und Konto",
    text: "Unterstützung bei deinem Tarif, der Abrechnung, Zahlungsfunktionen oder einer Kontolöschung.",
  },
];

const supportChecklist = [
  "E-Mail-Adresse deines Make-It-Booking-Kontos",
  "Name deiner Organisation und des betroffenen Standorts",
  "Kurze Beschreibung und, falls möglich, einen Screenshot",
  "Gerät und Browser oder App-Version, bei denen das Problem auftritt",
];

const faqItems = [
  {
    question: "Ich kann mich in der App nicht anmelden. Was kann ich tun?",
    answer:
      "Prüfe zuerst, ob du dieselbe E-Mail-Adresse wie in der Webanwendung verwendest. Setze dein Passwort über die Anmeldung zurück. Bleibt das Problem bestehen, sende uns die E-Mail-Adresse des Kontos und die angezeigte Fehlermeldung.",
  },
  {
    question: "Warum sehe ich einen Termin, Kunden oder Bereich nicht?",
    answer:
      "Inhalte hängen vom ausgewählten Standort, deinem Tarif und deinen Mitarbeiterberechtigungen ab. Prüfe den aktiven Standort. Organisationsinhaber können Berechtigungen in der Webanwendung unter Mitarbeiter kontrollieren.",
  },
  {
    question: "Wie melde ich einen technischen Fehler?",
    answer:
      "Beschreibe kurz, was du gemacht hast, was du erwartet hast und was stattdessen passiert ist. Ein Screenshot, die ungefähre Uhrzeit und das verwendete Gerät helfen uns bei der Prüfung.",
  },
  {
    question: "Wie kann ich mein Konto und meine Daten löschen lassen?",
    answer:
      "In der mobilen App findest du die Löschanfrage in den Einstellungen. Alternativ kannst du die öffentliche Seite zur Kontolöschung verwenden. Gesetzliche Aufbewahrungspflichten können für einzelne Daten gelten.",
  },
];

export default function Support() {
  const subject = encodeURIComponent("Supportanfrage Make It Booking");
  const body = encodeURIComponent(
    "Hallo Make It Booking Team,\n\nE-Mail des Kontos:\nOrganisation / Standort:\nGerät / Browser:\n\nWobei benötige ich Hilfe?\n\nWas ist passiert?\n\n"
  );
  const supportMail = `mailto:booking@makeit-media.de?subject=${subject}&body=${body}`;

  return (
    <main className="min-h-screen overflow-hidden bg-[#FFF9FB] text-[#21181D]">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[480px] bg-[radial-gradient(circle_at_18%_20%,rgba(226,188,204,0.5),transparent_34%),radial-gradient(circle_at_82%_0%,rgba(244,221,230,0.8),transparent_32%)]" />

      <header className="relative border-b border-[#2A1720]/10 bg-white/75 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" aria-label="Zur Startseite von Make It Booking">
            <img src="/images/mib-logo-full.png" alt="Make It Booking" className="h-9 w-auto sm:h-10" />
          </Link>
          <Link
            to="/login"
            className="rounded-full border border-[#2A1720]/15 bg-white px-4 py-2 text-sm font-semibold transition hover:border-[#2A1720]/30 hover:bg-[#FCF4F7]"
          >
            Zur Anmeldung
          </Link>
        </div>
      </header>

      <section className="relative px-4 pb-16 pt-10 sm:px-6 sm:pb-20 sm:pt-14 lg:px-8 lg:pt-20">
        <div className="mx-auto max-w-6xl">
          <Link
            to="/"
            className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-[#74636B] transition hover:text-[#21181D]"
          >
            <ArrowLeft className="h-4 w-4" /> Zurück zu Make It Booking
          </Link>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#7F4E64]/15 bg-white/80 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#7F4E64] shadow-sm">
              <MessageCircleMore className="h-4 w-4" /> Persönlicher Support
            </div>
            <h1 className="mt-6 text-3xl font-semibold tracking-[-0.045em] sm:text-4xl lg:text-5xl">
              Wie können wir dir helfen?
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-[#74636B] sm:text-lg sm:leading-8">
              Du hast eine Frage zu Make It Booking oder etwas funktioniert nicht wie erwartet? Beschreibe uns kurz dein Anliegen. Wir melden uns so schnell wie möglich bei dir.
            </p>
          </div>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href={supportMail}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#21181D] px-6 py-3.5 text-sm font-semibold text-white shadow-[0_16px_34px_rgba(55,31,42,0.2)] transition hover:-translate-y-0.5 hover:bg-black sm:w-auto"
            >
              <Mail className="h-4 w-4" /> Supportanfrage senden
            </a>
            <span className="text-center text-sm text-[#74636B] sm:text-left">
              booking@makeit-media.de
            </span>
          </div>
        </div>
      </section>

      <section className="relative px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {supportTopics.map(({ icon: Icon, eyebrow, title, text }) => (
              <article
                key={title}
                className="group rounded-[28px] border border-[#2A1720]/10 bg-white p-6 shadow-[0_12px_40px_rgba(63,40,50,0.06)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_20px_55px_rgba(63,40,50,0.1)] sm:p-7"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F6E8EE] text-[#70475A] transition group-hover:rotate-3 group-hover:bg-[#EFD8E2]">
                  <Icon className="h-5 w-5" />
                </div>
                <p className="mt-6 text-xs font-semibold uppercase tracking-[0.15em] text-[#9A7B89]">{eyebrow}</p>
                <h2 className="mt-2 text-xl font-semibold tracking-[-0.025em]">{title}</h2>
                <p className="mt-3 text-sm leading-6 text-[#74636B]">{text}</p>
              </article>
            ))}
          </div>

          <div className="mt-12 grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
            <section className="rounded-[30px] bg-[#21181D] p-6 text-white shadow-[0_24px_70px_rgba(45,24,34,0.18)] sm:p-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <h2 className="mt-6 text-2xl font-semibold tracking-[-0.035em]">Damit wir dir schnell helfen können</h2>
              <p className="mt-3 text-sm leading-6 text-white/65">
                Bitte sende keine Passwörter, vollständigen Patientendaten oder andere besonders sensible Inhalte per E-Mail.
              </p>
              <ul className="mt-6 space-y-4">
                {supportChecklist.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm leading-6 text-white/85">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#DDB6C8]" />
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-[30px] border border-[#2A1720]/10 bg-white p-6 shadow-[0_12px_40px_rgba(63,40,50,0.06)] sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#9A7B89]">Selbsthilfe</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-[-0.035em]">Häufige Fragen</h2>
              <div className="mt-6 divide-y divide-[#2A1720]/10">
                {faqItems.map(({ question, answer }) => (
                  <details key={question} className="group py-1">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 font-semibold marker:hidden">
                      <span>{question}</span>
                      <ChevronDown className="h-5 w-5 shrink-0 text-[#9A7B89] transition group-open:rotate-180" />
                    </summary>
                    <p className="pb-5 pr-8 text-sm leading-6 text-[#74636B]">{answer}</p>
                  </details>
                ))}
              </div>
            </section>
          </div>

          <section className="mt-12 overflow-hidden rounded-[30px] border border-[#CFA8BA]/35 bg-[#F6E8EE] p-6 sm:p-8 lg:flex lg:items-center lg:justify-between lg:gap-10">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#8B5A70]">Noch Fragen?</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-[-0.035em]">Wir schauen uns dein Anliegen persönlich an.</h2>
              <p className="mt-3 text-sm leading-6 text-[#74636B]">Nutze am besten die vorbereitete E-Mail, damit alle wichtigen Angaben direkt enthalten sind.</p>
            </div>
            <a
              href={supportMail}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-semibold shadow-sm transition hover:-translate-y-0.5 hover:shadow-md lg:mt-0 lg:w-auto"
            >
              E-Mail öffnen <ArrowUpRight className="h-4 w-4" />
            </a>
          </section>
        </div>
      </section>

      <footer className="border-t border-[#2A1720]/10 bg-white px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-6xl flex-col gap-5 text-sm text-[#74636B] sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} Make It Booking</p>
          <nav className="flex flex-wrap gap-x-5 gap-y-2" aria-label="Rechtliche Informationen">
            <Link to="/datenschutz" className="transition hover:text-[#21181D]">Datenschutz</Link>
            <Link to="/nutzungsbedingungen" className="transition hover:text-[#21181D]">Nutzungsbedingungen</Link>
            <Link to="/konto-loeschen" className="transition hover:text-[#21181D]">Kontolöschung</Link>
            <Link to="/impressum" className="transition hover:text-[#21181D]">Impressum</Link>
          </nav>
        </div>
      </footer>
    </main>
  );
}
